import { isNull, list, post, post_comment, sql, todo, user_collection } from "@famidex/powersync";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CAPA LIVE CENTRALIZADA (choke point único)                  │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Este módulo es el ÚNICO lugar donde el invariante «fila viva = `deleted_at IS
 * NULL`» se expresa para las tablas con papelera del canon (`todo`, `list`).
 * Toda lectura viva compone estos predicados (los builders de dominio en
 * todos/lists/theme/live los importan de aquí). Las ÚNICAS lecturas que ven
 * filas borradas son la papelera (`getTrashed*Query`) y algún lookup id-scoped
 * inofensivo, ambos declarados en la whitelist del guard (`guard-manifest.ts`).
 *
 * ¿Por qué centralizar? El invariante disperso fue la familia de bugs #1 de la
 * campaña (stats/backlinks/PATCH que ignoraban `deleted_at`). Con un choke point
 * único + guard anti-regresión, añadir una lectura cruda de estas tablas fuera
 * de la whitelist ROMPE el build (ver `live/__tests__/live-access-guard.test.ts`).
 *
 * NOTA de esquema (canon famidex): sólo `todo` y `list` llevan `deleted_at`.
 * `subtask` NO es soft-delete (se borra en cascada al purgar su `todo`/`list`
 * padre); por eso no tiene filtro live. Los clones cuya tabla hija SÍ tenga
 * papelera deben añadir su propio `liveXFilters()` aquí y su entrada en el
 * manifest.
 */

/**
 * Predicados de «tarea viva»: la propia tarea no está en la papelera y su lista
 * padre tampoco. Se aplica en TODA vista/consulta viva de `todo`.
 */
export function liveTodoFilters() {
  return [
    isNull(todo.deleted_at),
    sql`NOT EXISTS (SELECT 1 FROM ${list} WHERE ${list.id} = ${todo.list_id} AND ${list.deleted_at} IS NOT NULL)`,
  ];
}

/**
 * Predicados de «lista viva»: la lista no está en la papelera. Se aplica en
 * TODA vista/consulta viva de `list`.
 */
export function liveListFilters() {
  return [isNull(list.deleted_at)];
}

/**
 * Predicados de «ítem de colección vivo»: la fila no está en la papelera. Se
 * aplica en TODA vista/consulta viva de `user_collection` (colección privada
 * [L], 🎯 FamiDex). La ÚNICA lectura que ve filas borradas a propósito es el
 * lookup id/product-scoped del UPDATE-or-INSERT (re-añadir «des-borra» la fila)
 * y la papelera, ambos declarados `keyed`/`trash` en el guard-manifest.
 */
export function liveCollectionFilters() {
  return [isNull(user_collection.deleted_at)];
}

/**
 * Predicados de «publicación viva»: no está en la papelera. 📣 FamiDex comunidad
 * (Wave 1-C). Se aplica en TODA lectura viva de `post` (feed, galería, perfil).
 * La ÚNICA lectura que ve borradas es el lookup por id (`keyed`) del detalle.
 */
export function livePostFilters() {
  return [isNull(post.deleted_at)];
}

/** Predicados de «comentario vivo»: no está en la papelera. */
export function liveCommentFilters() {
  return [isNull(post_comment.deleted_at)];
}
